/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  useRef,
  useEffect,
  createContext,
} from 'react';
import { css, jsx } from '@emotion/core';
import setBlockProps from 'services/apiHelpers/setBlockProps';
import { colors } from 'styles/variables';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
import { addHotSpotOption } from 'factories/interactions/hotspot_isolated';
import { setBlocksAction } from 'actions/setBlocksAction';
import getStore from 'services/getStore';
import getBlockById from 'services/apiHelpers/getBlockById';
import HotspotControlBtn from './HotspotControlBtn';
import EditorBtn from 'components/UI/EditorBtn';
import { Block } from '@chameleoncreator/creator-app';
import Hotspot_Add from 'assets/Hotspot_Add.svg';
import Hotspot_Done from 'assets/Hotspot_Done.svg';
import imageUploadError from 'assets/imageUploadError_hotSpot.svg';
import BigSwitch from 'components/UI/BigSwitch';
import { H1 as H1_v2 } from '../EditorUtilities/UI/Headings';
import TogglePanel from '../EditorUtilities/UI/TogglePanel';
import CheckBox_v2 from '../EditorUtilities/UI/CheckBox';
import NumberPicker_v2 from '../EditorUtilities/UI/NumberPicker';
import ColorPicker_v2 from '../EditorUtilities/UI/ColorPicker';
import Info from '../EditorUtilities/UI/Info';
import { ImageLibraryContext } from '../EditImage/EditImage';
import ImageLibrary from '../EditImage/ImageLibrary';
import pubSub from 'services/pubSub';
import setNewBlockNewTree from 'services/apiHelpers/setNewBlockNewTree';
import {
  removeHotSpotOption,
  removeAllHotSpotOptions,
  getNextHotSpotBlock,
} from 'factories/interactions/hotspot_isolated';
import { getPolygonCenter } from './getPolygonCenter';

const store = getStore();
const { lPink, lGrey0, lGrey3, mint } = colors;

const togglePanelsCache = {};

export const UPDATE_RECTANGLE_HOTSPOT_LOCAL_STATE =
  'UPDATE_RECTANGLE_HOTSPOT_LOCAL_STATE';

const isEmpty = (obj: any[] | { [key: string]: any }): boolean => {
  return !!!(Array.isArray(obj) ? obj : Object.values(obj)).length;
};

const setUpdated = () => {
  return new Date().toString();
};

const EditHotSpot = ({ block }: { block?: any }) => {
  const {
    image,
    src,
    imageId = undefined,
    hotspotType,
    hotSpotColor,
    hotSpotSize,
    hotSpotsAnimated = true,
    hotSpotsVisible = true,
    useMediaInFeedback = true,
    showSeenState = true,
  } = block.props;

  const { id: hotspotsBlockId, children } = block;

  const [imageLibrary, setImageLibrary] = useState(false);

  // this is our actual interaction ' root '
  const hotSpotIsolatedParent = (Object.values(
    getStore().getState().blocks
  ).filter((b: any) => b.meta && b.meta.subType === 'HotSpot') as any).filter(
    (c: any) =>
      findByKeyVal_V2(c.id, 'type', 'HotSpotIsolated')!.id === hotspotsBlockId
  )[0];

  const { lockDown } = hotSpotIsolatedParent.props;

  const [currentHotspotId, setCurrentHotspotId] = useState<number | undefined>(
    undefined
  );

  const currentBlock = useRef<Block>(block);
  currentBlock.current = block;

  const currentHotspotBlock = useRef<Block | null>(null);
  currentHotspotBlock.current = currentHotspotId
    ? getBlockById(currentHotspotId)
    : null;

  const saveTogglePanelState = (panel, open) => {
    togglePanelsCache[hotspotsBlockId] = {
      ...togglePanelsCache[hotspotsBlockId],
      [panel]: open,
    };

    setBlockProps(block, { ...block.props });
  };

  const setHotspotType = shape => {
    // IdCounter = 0;
    const prevHotspotType = hotspotType;

    const [newBlock, newTree] = getNextHotSpotBlock(block)!;

    if (newBlock && newBlock.props) {
      (newBlock.children as Block[]).forEach(child => {
        const childBlock: Block = getBlockById(child.id);
        const points: any[] | any[][] = childBlock?.props?.points;

        let newPoints: any = [];

        if (prevHotspotType === 'circle') {
          // convert circles points to ' shape '
          if (shape === 'polygon') {
            newPoints = [
              [points[0], points[1] - 25],
              [points[0] + 25, points[1]],
              [points[0], points[1] + 25],
              [points[0] - 25, points[1]],
            ];
          }
        }

        if (prevHotspotType === 'polygon') {
          // convert polygon points to ' shape '
          if (shape === 'circle') {
            newPoints = getPolygonCenter(points);
          }
        }

        if (childBlock.props) {
          childBlock.props.points = newPoints;
          // this was broken : setUpdated;
          childBlock.props.updated = setUpdated();
        }
      });

      newBlock.props.hotspotType = shape;
    }

    setCurrentHotspotId(undefined);
    setNewBlockNewTree(newTree, newBlock, store, setBlocksAction);
  };

  const createNew = e => {
    const hotSpotId = `#${Math.random()
      .toString(16)
      .substr(2, 6)}`;

    const [newBlock, newTree] = addHotSpotOption(block, block.children.length, {
      name: `Hotspot ${hotSpotId}`,
      points: hotspotType === 'rectangle' ? {} : [],
      updated: setUpdated(),
    });

    if (newBlock === block) {
      return;
    }

    const store = getStore();

    setNewBlockNewTree(newTree, newBlock, store, setBlocksAction);

    if (newBlock && newBlock.children) {
      const lastChild = newBlock.children[newBlock.children.length - 1] as any;
      setCurrentHotspotId(lastChild?.id);
    }
  };

  const remove = (hotSpotBlock, index) => {
    const [newBlock, newTree] = removeHotSpotOption(hotSpotBlock, index);

    if (newBlock === hotSpotBlock) {
      return;
    }

    setNewBlockNewTree(newTree, newBlock, store, setBlocksAction);
  };

  useEffect(() => {
    return () => {
      if (currentHotspotBlock.current) {
        currentHotspotBlock.current = null;
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      // remove any hotspots that have no points
      const [newBlock, newTree] = getNextHotSpotBlock(currentBlock.current)!;

      const oldBlockHasChildren =
        currentBlock.current && Array.isArray(currentBlock.current.children);
      const newBlockHasChildren =
        newBlock.children && Array.isArray(newBlock.children);
      if (oldBlockHasChildren && newBlockHasChildren) {
        newBlock.children = (currentBlock.current.children as Block[]).filter(
          child => {
            const hotspot = getBlockById(child.id) as any;

            const { name, points } = hotspot?.props;
            //
            return !isEmpty(points);
          }
        );

        setNewBlockNewTree(newTree, newBlock, store, setBlocksAction);
      }
    };
  }, []);

  useEffect(() => {
    const pS = pubSub.subscribe(
      'UPDATE_HOTSPOT_POINTS',
      ({ points, updateRectangleHotspotLocalState = undefined }) => {
        //

        if (!currentHotspotBlock.current) {
          return;
        }

        if (hotspotType === 'circle') {
          setBlockProps(currentHotspotBlock.current, {
            points,
            updated: new Date().toString(),
          });
        }

        if (hotspotType === 'polygon') {
          const currentHotspotBlockPoints =
            currentHotspotBlock.current?.props?.points;

          setBlockProps(currentHotspotBlock.current, {
            points: [...currentHotspotBlockPoints, points],
            updated: setUpdated(),
          });
        }

        if (hotspotType === 'rectangle') {
          if (updateRectangleHotspotLocalState) {
            pubSub.publish(
              `${UPDATE_RECTANGLE_HOTSPOT_LOCAL_STATE}_hotspot-${currentHotspotBlock.current.id}`,
              points
            );

            return;
          }

          setBlockProps(currentHotspotBlock.current, {
            points,
            updated: setUpdated(),
          });
        }
      }
    );

    return () => {
      pS();
    };
  }, [currentHotspotBlock.current, hotspotType]);

  return (
    <_>
      <TogglePanel
        label={<H1_v2>interaction: settings</H1_v2>}
        toggle={
          togglePanelsCache[hotspotsBlockId]
            ? togglePanelsCache[hotspotsBlockId]['interaction: settings']
            : true
        }
        onToggle={open => saveTogglePanelState('interaction: settings', open)}
      >
        <CheckBox_v2
          label="Lock down"
          on={!!lockDown}
          onClick={() => {
            setBlockProps(hotSpotIsolatedParent, {
              lockDown: !!lockDown ? 0 : 1,
            });
          }}
        />
      </TogglePanel>

      <TogglePanel
        label={<H1_v2>hotspot: settings</H1_v2>}
        toggle={
          togglePanelsCache[hotspotsBlockId]
            ? togglePanelsCache[hotspotsBlockId]['hotspot: settings']
            : true
        }
        onToggle={open => saveTogglePanelState('hotspot: settings', open)}
      >
        <div
          css={css`
            margin: 15px 0 0 0;
          `}
        >
          <EditorBtn
            label="Insert image"
            style={{
              width: `${'175px' || '100%'}`,
              margin: '0 0 15px 0',
              border: `1px solid ${lGrey0}`,
              background: '#fff',
              color: lGrey0,
              fontSize: 11,

              hover: {
                color: '#fff',
                background: '#28292b',
              },
            }}
            css={css`
              min-height: 30px;
            `}
            onClick={() => {
              setImageLibrary(!imageLibrary);
            }}
          />
        </div>
        <div
          css={css`
            margin: 15px 0 0 0;
          `}
        >
          <Info
            info={`Select "Circle" to create round hot spots with an optional pulsing animation. Select "Polygon" to draw an outline around areas of interest.`}
          >
            Hotspot type
          </Info>
          <div
            css={css`
              margin: 10px 0 0 0;
            `}
          >
            <BigSwitch
              leftItem="Circle"
              rightItem="Polygon"
              leftValue="circle"
              rightValue="polygon"
              value={hotspotType}
              callback={value => {
                setHotspotType(value);
                return;
                //

                if (children.length > 0) {
                  if (
                    !window.confirm(
                      'Are you sure you want to switch? This is an undoable operation and your current content will be removed.'
                    )
                  )
                    return;
                }
                setHotspotType(value);
              }}
            />
          </div>
        </div>
        <ColorPicker_v2
          key="hotSpotColor"
          inColor={hotSpotColor}
          outColor={color => setBlockProps(block, { hotSpotColor: color })}
          label="Hotspot color"
        />
        {hotspotType === 'circle' && [
          <NumberPicker_v2
            key="hotSpotSize"
            value={hotSpotSize}
            increment={5}
            onChange={value => {
              setBlockProps(block, { hotSpotSize: value });
            }}
            label="Hotspot size"
          />,
          <CheckBox_v2
            key="hotSpotsAnimated"
            label="Hotspot animated"
            on={hotSpotsAnimated}
            onClick={() => {
              setBlockProps(block, { hotSpotsAnimated: !hotSpotsAnimated });
            }}
          />,
        ]}
        <CheckBox_v2
          label="Hotspot visibilty"
          on={hotSpotsVisible}
          onClick={() => {
            setBlockProps(block, { hotSpotsVisible: !hotSpotsVisible });
          }}
        />
        <div
          css={css`
            margin: 10px 0 0 0;
          `}
        >
          <CheckBox_v2
            label="Include media in feedback"
            on={useMediaInFeedback ? 1 : 0}
            onClick={() =>
              setBlockProps(block, {
                useMediaInFeedback: !useMediaInFeedback,
              })
            }
          />
        </div>

        <div
          css={css`
            margin: 10px 0 0 0;
          `}
        >
          <CheckBox_v2
            label="Show seen state"
            on={!!showSeenState}
            onClick={() =>
              setBlockProps(block, { showSeenState: !!showSeenState ? 0 : 1 })
            }
          />
        </div>

        {/* hiding the rectangle option for now */}
        {/* <CheckBox_v2
          label="Rectangle"
          on={hotspotType === 'rectangle'}
          onClick={() => {
            setHotspotType('rectangle');
          }}
        /> */}

        {(true || image) && (
          <div
            css={css`
              margin: 15px 0 0 0;
              border: 1px solid ${lGrey3};
              padding: 5px 0;
            `}
          >
            <div>
              {children.map((child, i) => {
                const { id } = child;
                const hotspot: Block = getBlockById(id);
                const { name, points } = hotspot?.props as any;

                const editing = currentHotspotId === id;

                return (
                  <HotspotControlBtn
                    key={id}
                    hotSpot={child}
                    label={name}
                    type="hoverRemove"
                    color={lGrey0}
                    rotation={45}
                    editing={editing}
                    onMouseOver={e => {
                      pubSub.publish(`ON_HOTSPOT_HOVER`, {
                        hotspotId: id,
                        hover: true,
                      });
                    }}
                    onMouseOut={e => {
                      pubSub.publish(`ON_HOTSPOT_HOVER`, {
                        hotspotId: id,
                        hover: false,
                      });
                    }}
                    updateLabel={value => {
                      // console.log({ value });
                      setBlockProps(hotspot, {
                        name: value,
                        updated: setUpdated(),
                      });
                    }}
                    onReDraw={e => {
                      if (block.props.hotspotType === 'polygon') {
                        // clear the points of this polygon
                        setBlockProps(hotspot, {
                          points: [],
                        });
                      }

                      setCurrentHotspotId(child.id);
                    }}
                    onClick={e => {
                      remove(block, i);
                    }}
                    css={css`
                      cursor: pointer;
                      pointer-events: ${currentHotspotId ? 'none' : 'auto'};
                      padding-right: 12px;
                      transition: background 0.2s;
                      background: ${currentHotspotId === id ? mint : ''};
                      &:hover {
                        background: #eaefff;
                        _color: ${lPink};
                      }
                    `}
                  />
                );
              })}
            </div>

            <div
              css={css`
            display: flex;
              align-items: center;
             width: calc(100% - 10px);
             height: 40px;
             background: url("${
               !currentHotspotId ? Hotspot_Add : Hotspot_Done
             }") no-repeat;
              background-size: 30px 30px;
              background-position: center right;
              padding: 0 10px;

              font-size: 12px;
             color: ${!currentHotspotId ? lPink : mint};

             cursor: pointer;

             &:hover {
               opacity: 0.8;
             }
           `}
              onClick={e => {
                !currentHotspotId
                  ? createNew(e)
                  : setCurrentHotspotId(undefined);
              }}
            >
              {!currentHotspotId ? 'Add new hotspot' : 'Done'}
            </div>
          </div>
        )}
      </TogglePanel>

      {imageLibrary && (
        <ImageLibraryContext.Provider
          value={{
            onAddImage(result) {
              const url = (result && result.url) || imageUploadError;

              setBlockProps(block, {
                src: url,
                imageId: result.id,
              });
            },
            onClose() {
              setImageLibrary(!imageLibrary);
            },
            currentImageSrc: src || image,
            currentImageId: imageId,
            cacheSelected: {
              yourLibrary: null,
              chameleonsLibrary: 0,
            },
            cacheFilter: {
              value: '',
            },
          }}
        >
          <ImageLibrary />
        </ImageLibraryContext.Provider>
      )}
    </_>
  );
};

export default EditHotSpot;
